import { useCallback } from "react";

/**
 * Custom hook for handling date and time conversions between API (usually UTC)
 * and the local browser timezone.
 */
export const useLocalTZ = () => {
    /**
     * Converts various timestamp formats into a valid local Date object.
     * Handles ISO strings, Unix timestamps (ms), and Date objects.
     */
    const toLocalDate = useCallback((timestamp: string | number | Date | null | undefined): Date | null => {
        if (!timestamp) return null;

        try {
            let normalized = timestamp;

            if (typeof normalized === "string") {
                const trimmed = normalized.trim();
                // Check for common TZ indicators: Z, GMT, UTC, +0530, -08:00
                const hasTzIndicator = /Z$|GMT|UTC|[+-]\d{2}(:?\d{2})?$/.test(trimmed);

                if (!hasTzIndicator) {
                    // Detect ISO-like "YYYY-MM-DD HH:mm:ss"
                    if (/^\d{4}[-/]\d{2}[-/]\d{2}/.test(trimmed)) {
                        normalized = trimmed.replace(" ", "T") + "Z";
                    } else {
                        // For human formats like "24 Feb 2026 06:47 AM", append UTC
                        normalized = `${trimmed} UTC`;
                    }
                }
            }

            const date = new Date(normalized);
            // Check if the date is valid
            return isNaN(date.getTime()) ? null : date;
        } catch (error) {
            console.error("useLocalTZ: Invalid date input", timestamp);
            return null;
        }
    }, []);

    /**
     * Formats a date to DD-MM-YYYY (Hyphenated)
     * Common usage: Tables, Dashboard display.
     */
    const formatDate = useCallback((timestamp: string | number | Date | null | undefined, separator: "-" | "/" = "-"): string => {
        const date = toLocalDate(timestamp);
        if (!date) return "-";

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}${separator}${month}${separator}${year}`;
    }, [toLocalDate]);

    /**
     * Formats a date to DD/MM/YYYY (Slashed)
     */
    const formatDateSlashed = useCallback((timestamp: string | number | Date | null | undefined): string => {
        return formatDate(timestamp, "/");
    }, [formatDate]);

    /**
     * Formats a date with time: DD-MM-YYYY HH:mm
     */
    const formatDateTime = useCallback((timestamp: string | number | Date | null | undefined): string => {
        const date = toLocalDate(timestamp);
        if (!date) return "-";

        const datePart = formatDate(date);
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${datePart} ${hours}:${minutes}`;
    }, [toLocalDate, formatDate]);

    /**
     * Checks if a given timestamp falls on "tomorrow" relative to current local time.
     * Useful for alerts and scheduling logic.
     */
    const isTomorrow = useCallback((timestamp: string | number | Date | null | undefined): boolean => {
        const date = toLocalDate(timestamp);
        if (!date) return false;

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        return (
            date.getDate() === tomorrow.getDate() &&
            date.getMonth() === tomorrow.getMonth() &&
            date.getFullYear() === tomorrow.getFullYear()
        );
    }, [toLocalDate]);

    /**
     * Checks if a given timestamp is today.
     */
    const isToday = useCallback((timestamp: string | number | Date | null | undefined): boolean => {
        const date = toLocalDate(timestamp);
        if (!date) return false;

        const today = new Date();

        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    }, [toLocalDate]);

    /**
   * Formats a date to DD Month YYYY (e.g., 24 Feb 2026)
   */
    const formatDateReadable = useCallback((timestamp: string | number | Date | null | undefined): string => {
        const date = toLocalDate(timestamp);
        if (!date) return "-";

        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    }, [toLocalDate]);

    /**
     * Formats time to hh:mm am/pm (lowercase)
     */
    const formatTime = useCallback((timestamp: string | number | Date | null | undefined): string => {
        const date = toLocalDate(timestamp);
        if (!date) return "-";

        return date
            .toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            })
            .toLowerCase();
    }, [toLocalDate]);

    /**
   * Formats a date to DD-MM-YY (Short)
   */
    const formatDateShort = useCallback((timestamp: string | number | Date | null | undefined): string => {
        const date = toLocalDate(timestamp);
        if (!date) return "-";

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = String(date.getFullYear()).slice(-2);

        return `${day}-${month}-${year}`;
    }, [toLocalDate]);

    return {
        toLocalDate,
        formatDate,
        formatDateSlashed,
        formatDateReadable,
        formatDateShort,
        formatDateTime,
        formatTime,
        isToday,
        isTomorrow,
    };
};

export default useLocalTZ;
